export type ArticleHeading = { id: string; text: string; level: 2 | 3 };

export function formatArticleDate(
  iso: string,
  locale: string,
  style: 'long' | 'short' = 'long',
): string {
  const value = String(iso ?? '').trim();
  if (!value) return '';
  return new Date(value).toLocaleDateString(locale === 'en' ? 'en-US' : locale, {
    year: 'numeric',
    month: style === 'long' ? 'long' : 'short',
    day: 'numeric',
  });
}

export function getArticleDateFields(
  publishedAt?: string | null,
  updatedAt?: string | null,
): {
  publishedIso: string | null;
  updatedIso: string | null;
} {
  const publishedIso = String(publishedAt ?? '').trim() || null;
  const updatedIsoRaw = String(updatedAt ?? '').trim() || null;
  const updatedIso =
    updatedIsoRaw &&
    publishedIso &&
    new Date(updatedIsoRaw).getTime() > new Date(publishedIso).getTime() + 60_000
      ? updatedIsoRaw
      : null;

  return {
    publishedIso: publishedIso ?? updatedIsoRaw,
    updatedIso,
  };
}

export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function normalizeTitleForCompare(title: string): string {
  return title.toLowerCase().replace(/\s+/g, ' ').trim();
}

function normalizeHeadingForCompare(heading: string): string {
  return normalizeTitleForCompare(
    heading
      .replace(/<[^>]+>/g, '')
      .replace(/\*\*|__/g, '')
      .replace(/\s*\{#.+?\}\s*$/, ''),
  );
}

function headingMatchesTitle(heading: string, articleTitle: string): boolean {
  return normalizeHeadingForCompare(heading) === normalizeTitleForCompare(articleTitle);
}

function stripLeadingBlankLines(lines: string[]): number {
  let start = 0;
  while (start < lines.length && !lines[start].trim()) start += 1;
  return start;
}

function removeLeadingBlankLines(lines: string[], start: number): number {
  while (start < lines.length && !lines[start].trim()) lines.splice(start, 1);
  return start;
}

export function removeDuplicateArticleHeading(content: string, articleTitle?: string | null): string {
  if (!articleTitle) return content;

  let normalized = String(content ?? '').replace(/\r\n/g, '\n');

  normalized = normalized.replace(
    /^\s*<h1(?:\s[^>]*)?>([\s\S]*?)<\/h1>\s*/i,
    (match, inner: string) =>
      headingMatchesTitle(String(inner ?? ''), articleTitle) ? '' : match,
  );

  const lines = normalized.split('\n');
  let start = stripLeadingBlankLines(lines);

  if (start < lines.length) {
    const markdownMatch = lines[start].trim().match(/^(#{1,2})\s+(.+)$/);
    if (markdownMatch && headingMatchesTitle(markdownMatch[2], articleTitle)) {
      lines.splice(start, 1);
      start = removeLeadingBlankLines(lines, start);
    }
  }

  if (start < lines.length && start + 1 < lines.length) {
    const titleLine = lines[start].trim();
    const underline = lines[start + 1].trim();
    if (titleLine && /^=+$/.test(underline) && headingMatchesTitle(titleLine, articleTitle)) {
      lines.splice(start, 2);
      removeLeadingBlankLines(lines, start);
    }
  }

  return lines.join('\n');
}

function normalizeRunOnListParagraphs(content: string): string {
  return content
    .replace(/\r\n/g, '\n')
    .split('\n')
    .flatMap((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
        return [line];
      }

      const parts = trimmed.split(/\s+(?=The )/).map((part) => part.trim()).filter(Boolean);
      if (parts.length >= 3 && parts.every((part) => /^The /i.test(part))) {
        return parts.map((part) => `- ${part}`);
      }

      return [line];
    })
    .join('\n');
}

function normalizePlainTextListBlocks(content: string): string {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const result: string[] = [];

  for (let i = 0; i < lines.length; ) {
    const line = lines[i];
    const trimmed = line.trim();
    const listLines: string[] = [];
    let j = i + 1;

    while (j < lines.length) {
      const next = lines[j].trim();
      if (!next) break;
      if (/^[-*]\s/.test(next) || /^\d+\.\s/.test(next) || next.startsWith('#')) break;
      if (/^The /i.test(next)) {
        listLines.push(next);
        j += 1;
        continue;
      }
      break;
    }

    if (trimmed && listLines.length >= 2) {
      result.push(line);
      result.push('');
      for (const item of listLines) result.push(`- ${item}`);
      i = j;
      continue;
    }

    result.push(line);
    i += 1;
  }

  return result.join('\n');
}

export function normalizeBlogArticleContent(content: string, articleTitle?: string | null): string {
  let normalized = String(content ?? '').replace(/\r\n/g, '\n');
  normalized = removeDuplicateArticleHeading(normalized, articleTitle);
  normalized = normalizeRunOnListParagraphs(normalized);
  normalized = normalizePlainTextListBlocks(normalized);
  normalized = normalized.replace(/^_([\s\S]+?)\n_\s*$/g, '_$1_');
  return normalized;
}

export function extractArticleHeadings(content: string): ArticleHeading[] {
  const headings: ArticleHeading[] = [];

  for (const line of content.replace(/\r\n/g, '\n').split('\n')) {
    const match = line.trim().match(/^(#{2,3})\s+(.+)$/);
    if (!match) continue;

    const level = match[1].length;
    if (level !== 2 && level !== 3) continue;

    const text = match[2].replace(/\*\*|__/g, '').trim();
    headings.push({ id: slugifyHeading(text), text, level });
  }

  return headings;
}

export function isGenericImageAlt(alt?: string | null): boolean {
  if (!alt) return true;
  const trimmed = alt.trim();
  if (!trimmed) return true;
  return /^image\d*\.(webp|jpe?g|png|gif|svg)$/i.test(trimmed) || /^img_\d+/i.test(trimmed);
}

export function resolveImageAlt(alt?: string | null): string {
  if (!isGenericImageAlt(alt)) return alt!.trim();
  return '';
}
